import { useLocale, type Locale } from '@/lib/locale'

/**
 * Bilingual copy for the Qatar face's own pages (header, home, about,
 * contact, footer). Shared pages (course player, library, tools) read
 * lib/i18n/ui.ts; slides read their own copy files.
 */
const en = {
  // Header
  navCourse: 'The Course',
  navLibrary: 'Scam Library',
  navTools: 'Tools',
  navAbout: 'About',
  navReport: 'Report fraud',
  startCourse: 'Start the course',
  menuCourse: 'Scam Defense Course',
  menuResources: 'Tools & Resources',

  // Home: hero
  heroEyebrow: 'For our customers and their families. No account needed.',
  heroTitle1: 'Avoid scams.',
  heroTitle2: 'Protect your money.',
  heroBody: 'Antivirus cannot protect you from a well-crafted lie. Defensive training can.',
  heroBrowse: 'Browse the scam library',
  heroCheck: 'Check a suspicious message',
  heroTrust: 'Provided to customers by',

  // Home: program band
  programTitle: 'Build the skills to defend yourself.',
  programBody: 'Practical training to help you and your family outsmart modern scammers, in Arabic or English.',
  benefit1Title: 'Spot deceptions faster',
  benefit1Body: 'and take steps to avoid them',
  benefit2Title: 'Build safe habits',
  benefit2Body: 'that protect you under pressure',
  benefit3Title: 'Know what to do',
  benefit3Body: 'if something ever feels wrong',

  // Home: course
  courseEyebrow: 'The course',
  courseTitle: 'Scam Defense Fundamentals',
  courseBody:
    'A 40-minute course covering the patterns behind nearly every modern scam. Plain language, local examples, no account needed.',

  // Home: library
  libraryEyebrow: 'The scam library',
  libraryTitle: 'The scams reaching Qatar right now.',
  libraryBody:
    'How each one works, the opening lines to recognize, and exactly what to do. Written for the messages our customers actually receive.',
  libraryBrowse: 'Browse the full library',

  // Home: tools
  toolsEyebrow: 'Tools',
  toolsTitle: 'Check something before you act.',
  toolsBody: 'Something feels a little off? Check it here first, no account needed.',
  toolScamTitle: 'Scam Checker',
  toolScamBody:
    'Paste a suspicious link, message, voice note, or video and get a plain-language read on whether it looks genuine, including signs of AI voice cloning and deepfakes.',
  toolPasswordTitle: 'Password Check',
  toolPasswordBody:
    'Test the strength of a password and see if it has appeared in a data breach. Runs entirely on your device.',
  openTool: 'Open tool',

  // Home: report band
  reportTitle: 'Think you have been targeted?',
  reportBody:
    'Call our fraud line first, day or night. Then report it to the Ministry of Interior so it can be stopped for others.',
  reportCallLabel: 'Fraud line, 24 hours',
  reportMoiLabel: 'Ministry of Interior cybercrime hotline',
  reportMoiNumber: '2347444',
  reportPolice: 'Emergency: 999',

  // Home: articles
  articlesTitle: 'Recent articles',
  articlesBody: 'The latest on personal security and scam defense.',
  seeAll: 'See all',
  read: 'Read',

  // Footer
  footerPowered: 'Powered by GranGuard',
  footerLegal: 'Educational content. Not legal or financial advice.',
  footerPrivacy: 'Privacy',
  footerAbout: 'About',
  footerFraudLine: 'Fraud line',

  // About
  aboutEyebrow: 'About this program',
  aboutTitle: 'Scam defense for every customer.',
  aboutIntro:
    'This site is a service for customers and their families. It teaches the patterns behind modern fraud, keeps a library of the scams circulating in Qatar, and gives you tools for the moment something feels wrong.',
  aboutWhyTitle: 'Why we offer it',
  aboutWhyBody:
    'Most fraud losses today do not come from hacked systems. They come from a convincing phone call, message, or video that persuades someone to move their own money. The best protection is a customer who recognizes the pattern and pauses before acting. Training does that; software alone cannot.',
  aboutWhatTitle: 'What is inside',
  aboutWhat1Title: 'A six-chapter course',
  aboutWhat1Body: 'About 40 minutes in total. Short screens, real examples, no account.',
  aboutWhat2Title: 'A living scam library',
  aboutWhat2Body: 'The scams circulating in Qatar, how they work, and what to do.',
  aboutWhat3Title: 'Tools for the moment of doubt',
  aboutWhat3Body: 'A scam checker for links, messages, voice notes, and video, and a password check.',
  aboutWhat4Title: 'Arabic and English',
  aboutWhat4Body: 'Every screen is available in both languages. Switch at any time.',
  aboutPrivacyTitle: 'Your privacy',
  aboutPrivacyBody:
    'The site needs no login and is not connected to your bank accounts. Nothing you paste into the scam checker is published or linked to you. Course progress is stored only on your own device.',
  aboutPoweredTitle: 'Who builds it',
  aboutPoweredBody:
    'The training, the scam library, and the checking tools are built and kept current by a team of cyber security professionals who specialise in fraud and social engineering. Your bank brands the program and makes it available to its customers.',
  aboutCta: 'Start the course',

  // Contact
  contactEyebrow: 'Contact',
  contactTitle: 'Reach us the moment something feels wrong.',
  contactIntro:
    'If money has moved, or you shared a code, password, or card details, call the fraud line first. Every minute matters for a recall.',
  contactFraudTitle: 'Fraud line',
  contactFraudBody: 'Open 24 hours, every day. Say "fraud" to be routed straight to the team.',
  contactEmailTitle: 'Report a suspicious message',
  contactEmailBody: 'Forward suspicious emails, screenshots, or numbers to our fraud team.',
  contactMoiTitle: 'Ministry of Interior',
  contactMoiBody:
    'Report cybercrime to the MOI through the Metrash2 app, the hotline, or by email. Reporting helps stop the same scam reaching others.',
  contactMoiHotline: 'Hotline 2347444',
  contactMoiWhatsapp: 'WhatsApp 66815757',
  contactMoiEmail: 'cccc@moi.gov.qa',
  contactPoliceTitle: 'Emergency',
  contactPoliceBody: 'If you are in immediate danger, call 999.',
  contactStepsTitle: 'What to do in the first hour',
  contactSteps: [
    'Stop all contact with the person or number. Do not reply, even to say no.',
    'Call the fraud line and say a transfer or payment needs to be stopped.',
    'Change the passwords for your banking app, email, and Metrash2, in that order.',
    'Save screenshots, numbers, and any receipts before deleting anything.',
    'Tell someone you trust. Scammers count on silence.',
  ],
  contactChapterCta: 'Open the full recovery chapter',
}

export type QatarCopy = typeof en

const ar: QatarCopy = {
  navCourse: 'الدورة',
  navLibrary: 'مكتبة الاحتيال',
  navTools: 'الأدوات',
  navAbout: 'عن البرنامج',
  navReport: 'الإبلاغ عن احتيال',
  startCourse: 'ابدأ الدورة',
  menuCourse: 'دورة الحماية من الاحتيال',
  menuResources: 'الأدوات والمصادر',

  heroEyebrow: 'لعملائنا وعائلاتهم. لا حاجة إلى حساب.',
  heroTitle1: 'تجنّب الاحتيال.',
  heroTitle2: 'احمِ أموالك.',
  heroBody: 'برامج مكافحة الفيروسات لا تحميك من كذبة محكمة. التدريب الدفاعي يفعل.',
  heroBrowse: 'تصفّح مكتبة الاحتيال',
  heroCheck: 'افحص رسالة مريبة',
  heroTrust: 'خدمة للعملاء مقدّمة من',

  programTitle: 'اكتسب المهارات التي تحميك.',
  programBody: 'تدريب عملي يساعدك أنت وعائلتك على التفوّق على المحتالين المعاصرين، بالعربية أو الإنجليزية.',
  benefit1Title: 'اكشف الخداع أسرع',
  benefit1Body: 'واتخذ خطوات لتجنّبه',
  benefit2Title: 'ابنِ عادات آمنة',
  benefit2Body: 'تحميك تحت الضغط',
  benefit3Title: 'اعرف ما تفعله',
  benefit3Body: 'إذا شعرت يوماً أن هناك خطأً ما',

  courseEyebrow: 'الدورة',
  courseTitle: 'أساسيات الحماية من الاحتيال',
  courseBody:
    'دورة مدتها 40 دقيقة تغطي الأنماط التي تقف خلف معظم عمليات الاحتيال الحديثة. لغة واضحة، أمثلة محلية، ولا حاجة إلى حساب.',

  libraryEyebrow: 'مكتبة الاحتيال',
  libraryTitle: 'عمليات الاحتيال التي تصل إلى قطر الآن.',
  libraryBody:
    'كيف تعمل كل واحدة منها، والعبارات الافتتاحية التي عليك تمييزها، وما الذي تفعله بالضبط. كُتبت للرسائل التي يتلقاها عملاؤنا فعلاً.',
  libraryBrowse: 'تصفّح المكتبة كاملة',

  toolsEyebrow: 'الأدوات',
  toolsTitle: 'تحقّق قبل أن تتصرّف.',
  toolsBody: 'هل تشعر أن شيئاً ما ليس على ما يرام؟ افحصه هنا أولاً، دون الحاجة إلى حساب.',
  toolScamTitle: 'فاحص الاحتيال',
  toolScamBody:
    'الصق رابطاً أو رسالة أو ملاحظة صوتية أو فيديو مريباً واحصل على تقييم واضح لما إذا كان يبدو حقيقياً، بما في ذلك علامات استنساخ الصوت بالذكاء الاصطناعي والتزييف العميق.',
  toolPasswordTitle: 'فحص كلمة المرور',
  toolPasswordBody:
    'اختبر قوة كلمة المرور وتحقّق مما إذا كانت قد ظهرت في تسريب بيانات. يعمل بالكامل على جهازك.',
  openTool: 'افتح الأداة',

  reportTitle: 'هل تعتقد أنك مستهدف؟',
  reportBody:
    'اتصل بخط الاحتيال أولاً، ليلاً أو نهاراً. ثم أبلغ وزارة الداخلية ليتم إيقافه قبل أن يصل إلى غيرك.',
  reportCallLabel: 'خط الاحتيال، على مدار الساعة',
  reportMoiLabel: 'الخط الساخن للجرائم الإلكترونية في وزارة الداخلية',
  reportMoiNumber: '2347444',
  reportPolice: 'الطوارئ: 999',

  articlesTitle: 'أحدث المقالات',
  articlesBody: 'آخر ما كُتب عن الأمان الشخصي والحماية من الاحتيال.',
  seeAll: 'عرض الكل',
  read: 'اقرأ',

  footerPowered: 'مدعوم من GranGuard',
  footerLegal: 'محتوى تعليمي. ليس نصيحة قانونية أو مالية.',
  footerPrivacy: 'الخصوصية',
  footerAbout: 'عن البرنامج',
  footerFraudLine: 'خط الاحتيال',

  aboutEyebrow: 'عن هذا البرنامج',
  aboutTitle: 'حماية من الاحتيال لكل عميل.',
  aboutIntro:
    'هذا الموقع خدمة للعملاء وعائلاتهم. يعلّم الأنماط التي تقف خلف الاحتيال الحديث، ويحتفظ بمكتبة لعمليات الاحتيال المنتشرة في قطر، ويمنحك أدوات للحظة التي تشعر فيها أن هناك خطأً ما.',
  aboutWhyTitle: 'لماذا نقدّمه',
  aboutWhyBody:
    'معظم خسائر الاحتيال اليوم لا تأتي من أنظمة مخترقة، بل من مكالمة أو رسالة أو فيديو مقنع يدفع شخصاً إلى تحويل أمواله بنفسه. أفضل حماية هي عميل يتعرّف على النمط ويتوقّف قبل أن يتصرّف. التدريب يحقق ذلك، والبرمجيات وحدها لا تستطيع.',
  aboutWhatTitle: 'ماذا يتضمّن',
  aboutWhat1Title: 'دورة من ستة فصول',
  aboutWhat1Body: 'نحو 40 دقيقة في المجموع. شاشات قصيرة، أمثلة حقيقية، ولا حاجة إلى حساب.',
  aboutWhat2Title: 'مكتبة احتيال محدّثة باستمرار',
  aboutWhat2Body: 'عمليات الاحتيال المنتشرة في قطر، وكيف تعمل، وما الذي تفعله.',
  aboutWhat3Title: 'أدوات للحظة الشك',
  aboutWhat3Body: 'فاحص احتيال للروابط والرسائل والملاحظات الصوتية والفيديو، وفحص لكلمة المرور.',
  aboutWhat4Title: 'بالعربية والإنجليزية',
  aboutWhat4Body: 'كل شاشة متاحة باللغتين. بدّل بينهما في أي وقت.',
  aboutPrivacyTitle: 'خصوصيتك',
  aboutPrivacyBody:
    'لا يحتاج الموقع إلى تسجيل دخول وليس متصلاً بحساباتك المصرفية. لا يُنشر أي شيء تلصقه في فاحص الاحتيال ولا يُربط بك. ويُحفظ تقدّمك في الدورة على جهازك فقط.',
  aboutPoweredTitle: 'من يبنيه',
  aboutPoweredBody:
    'التدريب ومكتبة الاحتيال وأدوات الفحص من بناء فريق من خبراء الأمن السيبراني المتخصصين في الاحتيال والهندسة الاجتماعية، ويتولون تحديثها باستمرار. ويقدّم بنكك البرنامج لعملائه تحت علامته.',
  aboutCta: 'ابدأ الدورة',

  contactEyebrow: 'تواصل معنا',
  contactTitle: 'تواصل معنا في اللحظة التي تشعر فيها أن هناك خطأً ما.',
  contactIntro:
    'إذا جرى تحويل مال، أو شاركت رمزاً أو كلمة مرور أو بيانات بطاقة، فاتصل بخط الاحتيال أولاً. كل دقيقة مهمة لاسترداد المبلغ.',
  contactFraudTitle: 'خط الاحتيال',
  contactFraudBody: 'متاح على مدار الساعة كل يوم. قل "احتيال" ليتم تحويلك مباشرة إلى الفريق المختص.',
  contactEmailTitle: 'الإبلاغ عن رسالة مريبة',
  contactEmailBody: 'أعد توجيه رسائل البريد المريبة أو لقطات الشاشة أو الأرقام إلى فريق مكافحة الاحتيال لدينا.',
  contactMoiTitle: 'وزارة الداخلية',
  contactMoiBody:
    'أبلغ عن الجرائم الإلكترونية لوزارة الداخلية عبر تطبيق مطراش2 أو الخط الساخن أو البريد الإلكتروني. الإبلاغ يساعد على منع وصول الاحتيال نفسه إلى الآخرين.',
  contactMoiHotline: 'الخط الساخن 2347444',
  contactMoiWhatsapp: 'واتساب 66815757',
  contactMoiEmail: 'cccc@moi.gov.qa',
  contactPoliceTitle: 'الطوارئ',
  contactPoliceBody: 'إذا كنت في خطر مباشر، اتصل بالرقم 999.',
  contactStepsTitle: 'ما تفعله في الساعة الأولى',
  contactSteps: [
    'أوقف كل تواصل مع الشخص أو الرقم. لا ترد، حتى لو كان الرد رفضاً.',
    'اتصل بخط الاحتيال وقل إن هناك تحويلاً أو دفعة يجب إيقافها.',
    'غيّر كلمات المرور لتطبيق البنك والبريد الإلكتروني ومطراش2، بهذا الترتيب.',
    'احفظ لقطات الشاشة والأرقام وأي إيصالات قبل حذف أي شيء.',
    'أخبر شخصاً تثق به. المحتالون يعوّلون على الصمت.',
  ],
  contactChapterCta: 'افتح فصل التعافي كاملاً',
}

const DICT: Record<Locale, QatarCopy> = { en, ar }

export function useQatarCopy(): QatarCopy {
  return DICT[useLocale()]
}
